import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CardComponent } from '@app/components/card';
import { SpinnerComponent } from '@app/components/spinner';
import {
  TextColor,
  TextComponent,
  TextHeight,
  TextSize,
  TextSpacing,
  TextType,
  TextWeight,
} from '@app/components/text';
import { TitleComponent, TitleSpacing } from '@app/components/title-section';
import { ToastService, ToastType } from '@app/components/toast';
import {
  ViewComponent,
  ViewDirection,
  ViewSpacing,
} from '@app/components/view';
import {
  Challenge,
  ChallengeService,
  ChallengeTeam,
  ChallengeTeamMember,
} from '@app/services/challenge/challenge.service';
import { Logger } from '@app/services/logger/logger.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { Spacing } from '@app/shared/types';
import { catchError, forkJoin, of } from 'rxjs';

@Component({
  selector: 'app-step-challenge-leaderboard',
  standalone: true,
  imports: [
    CardComponent,
    CommonModule,
    TitleComponent,
    TextComponent,
    ViewComponent,
    SpinnerComponent,
  ],
  templateUrl: './step-challenge-leaderboard.component.html',
  styleUrl: './step-challenge-leaderboard.component.css',
})
export class StepChallengeLeaderboardComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly challengeService = inject(ChallengeService);
  private readonly logger = inject(Logger);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);

  isLoading = signal(true);
  challenge = signal<Challenge | null>(null);
  team = signal<ChallengeTeam | null>(null);
  members = signal<ChallengeTeamMember[]>([]);
  TextType = TextType;
  TextSize = TextSize;
  TextHeight = TextHeight;
  TextWeight = TextWeight;
  TextColor = TextColor;
  TextSpacing = TextSpacing;
  ViewDirection = ViewDirection;
  ViewSpacing = ViewSpacing;
  TitleSpacing = TitleSpacing;
  Spacing = Spacing;

  ngOnInit() {
    const challengeId = this.route.snapshot.paramMap.get('id');
    const teamId = this.route.snapshot.paramMap.get('teamId');
    if (!challengeId || !teamId) {
      this.goBack();
      return;
    }

    forkJoin({
      challenge: this.challengeService.getStepChallenge(challengeId),
      teams: this.challengeService.getChallengeTeamsWithMembers(challengeId),
    })
      .pipe(
        catchError((err) => {
          this.logger.error('Error loading challenge leaderboard');
          this.logger.error(JSON.stringify(err, null, 2));
          this.toastService.add(
            this.translatorService.translate(
              'challenges.challengeCouldNotBeLoaded'
            ),
            ToastType.ERROR
          );
          this.goBack();
          return of({ challenge: null, teams: [] });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(({ challenge, teams }) => {
        this.isLoading.set(false);
        if (!challenge) {
          return;
        }
        const team = teams.find(
          (item) => this.challengeService.getTeamId(item) === teamId
        );
        if (!team) {
          this.goBack();
          return;
        }
        this.challenge.set({ ...challenge, teams });
        this.team.set(team);
        this.members.set(team.members ?? []);
      });
  }

  getOrdinalSuffix(rank?: number): string {
    if (!rank) {
      return '';
    }
    if (rank % 100 >= 11 && rank % 100 <= 13) {
      return `th Place`;
    }

    const lastDigit = rank % 10;
    switch (lastDigit) {
      case 1:
        return `st Place`;
      case 2:
        return `nd Place`;
      case 3:
        return `rd Place`;
      default:
        return `th Place`;
    }
  }

  goBack(): void {
    const challengeId = this.route.snapshot.paramMap.get('id');
    if (challengeId) {
      this.router.navigate(['challenges', 'steps', challengeId]);
      return;
    }
    this.router.navigate(['challenges']);
  }
}
